// Object library (owner, 2026-09-30: "create a library of objects to add in to plans like
// pumps and filters and manifolds ... presets I can keep with Ozcrete so different
// accounts can have their own libraries ... selecting a group of objects and grouping
// them and saving it as a preset object").
//
// A library item is a clipboard snapshot with a name: the same entities copy/paste
// already handles (groups and their children included), moved so the item's CENTRE is
// at 0,0. Placing one is a paste at the point you click. Pure — the tests hold it.
import type { Vec } from "../core/geometry";
import type { Doc, Entity, Units } from "../doc/entities";
import { entityBounds } from "../engine/bounds";
import { copyEntities, translateInPlace } from "../tools/editOps";

export interface LibraryItem {
  id: string;
  name: string;
  category: string;
  /** Centred on 0,0, in millimetres (the document's internal unit). */
  entities: Entity[];
  /** The item's width and height, for the list and the preview. */
  size: { w: number; h: number };
  /** The drawing units it was saved from — shown, never used to rescale. */
  units: Units;
  updatedAt: string;
}

/** Where an item is kept. "account" = the Eden account's library, shared by everyone in it. */
export type LibrarySource = "account" | "local";

/**
 * 3.30.0: the plant and equipment of a pool plant room, one category each. Owner, 2026-10-27: "the library needs to
 * have categories for all plant and equipment". A person can also type a new category (CategoryPicker).
 */
export const PLANT_CATEGORIES = [
  "Pumps",
  "Filters",
  "Strainers and lint pots",
  "Chlorinators",
  "Chemical dosing",
  "Chemical controllers",
  "UV systems",
  "Ozone systems",
  "Heating · heat pumps",
  "Heating · boilers and heat exchangers",
  "Balance tanks",
  "Tanks",
  "Backwash and waste",
  "Valves",
  "Manifolds",
  "Flow meters and instruments",
  "Electrical · switchboards and drives",
  "Equipment"
];

/** The built-in groups of one maker, listed right after their plant category (the Library panel shows groups in this order). */
const MAKER_GROUPS: Record<string, string[]> = {
  Pumps: ["Pumps · Davey CF (bare shaft)", "Pumps · Davey CM 2 pole (2900 rpm)", "Pumps · Davey CM 4 pole (1450 rpm)"],
  Chlorinators: ["Chlorinators · AIS AutoChlor"]
};

export const DEFAULT_CATEGORIES = [
  ...PLANT_CATEGORIES.flatMap((c) => [c, ...(MAKER_GROUPS[c] ?? [])]),
  "Fittings",
  "Imported",
  "Pipe fittings · 90° bends",
  "Pipe fittings · 45° bends",
  "Pipe fittings · 90° bends, up",
  "Pipe fittings · 90° bends, down",
  "Pipe fittings · End caps",
  "Pipe fittings · Tees",
  "Pipe fittings · Tees, branch up",
  "Pipe fittings · Tees, branch down",
  "Pipe fittings · Reducers",
  "Pipe fittings · Lint pots",
  "Pipe fittings · Check valves",
  "Other"
];

/**
 * 3.30.0: the categories to choose from: the standard ones and every category already in use (`used`), each once, in
 * name order (numbers in number order). Empty names are left out.
 */
export function categoryChoices(used: Iterable<string> = []): string[] {
  const all = new Set<string>();
  for (const c of [...DEFAULT_CATEGORIES, ...used]) {
    const t = c.trim();
    if (t) all.add(t);
  }
  return [...all].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
}

/** Image entities need their picture files, which a library item does not carry. */
const NOT_IN_LIBRARY = new Set(["image"]);

/** Make an item from the selection, or say why not. */
export function makeLibraryItem(
  doc: Doc,
  ids: Iterable<string>,
  name: string,
  category: string,
  id: string,
  now: string,
  makeId: () => string = () => crypto.randomUUID()
): { item: LibraryItem } | { error: string } {
  const clean = name.trim();
  if (!clean) return { error: "Give the object a name." };
  const clip = copyEntities(doc, ids);
  if (clip.entities.length === 0) return { error: "Select the objects to save first." };
  if (clip.entities.some((e) => NOT_IN_LIBRARY.has(e.type))) {
    return { error: "Photos and plans cannot go in the library. Select only lines, shapes and text." };
  }
  // Bounds of the top-level entities (a group's bounds already cover its children).
  const childIds = new Set(clip.entities.flatMap((e) => (e.type === "group" ? e.children : [])));
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const e of clip.entities) {
    if (childIds.has(e.id)) continue;
    const b = entityBounds(e, doc);
    if (b.minX > b.maxX) continue;
    minX = Math.min(minX, b.minX);
    minY = Math.min(minY, b.minY);
    maxX = Math.max(maxX, b.maxX);
    maxY = Math.max(maxY, b.maxY);
  }
  if (!(minX <= maxX)) return { error: "The selection has no size to save." };
  const centre: Vec = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
  const entities = clip.entities.map((e) => {
    const c = structuredClone(e);
    translateInPlace(c, { x: -centre.x, y: -centre.y });
    c.locked = false;
    c.hidden = false;
    return c;
  });
  // SEVERAL objects are saved as ONE group, so a placed pump is one object you can move.
  const tops = entities.filter((e) => !childIds.has(e.id));
  if (tops.length > 1) {
    entities.push({ id: makeId(), type: "group", layerId: tops[0]!.layerId, locked: false, hidden: false, children: tops.map((e) => e.id) });
  }
  return {
    item: {
      id,
      name: clean,
      category: category.trim() || "Other",
      entities,
      size: { w: maxX - minX, h: maxY - minY },
      units: doc.units,
      updatedAt: now
    }
  };
}

/** Is this a usable item? (Anything read back from storage or Eden goes through here.) */
export function isLibraryItem(v: unknown): v is LibraryItem {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.id === "string" &&
    typeof o.name === "string" &&
    typeof o.category === "string" &&
    Array.isArray(o.entities) &&
    o.entities.length > 0 &&
    !!o.size &&
    typeof (o.size as { w?: unknown }).w === "number"
  );
}

/** Items by category, categories in a stable order, names A–Z. */
export function groupByCategory<T extends { item: LibraryItem }>(rows: T[]): Array<{ category: string; rows: T[] }> {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const list = map.get(r.item.category) ?? [];
    list.push(r);
    map.set(r.item.category, list);
  }
  const rank = (c: string) => {
    const i = DEFAULT_CATEGORIES.indexOf(c);
    return i < 0 ? DEFAULT_CATEGORIES.length - 1 : i; // unknown categories sit before "Other"
  };
  return [...map.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([category, list]) => ({ category, rows: list.sort((a, b) => a.item.name.localeCompare(b.item.name, undefined, { numeric: true })) }));
}

// ---------------------------------------------------------------- preview --

const f = (n: number) => Number(n.toFixed(2));

/** A small line drawing of the item for the library list. Lines, shapes and arcs only. */
export function thumbnailSvg(item: LibraryItem): string {
  const byId = new Map(item.entities.map((e) => [e.id, e]));
  const childIds = new Set(item.entities.flatMap((e) => (e.type === "group" ? e.children : [])));
  const parts: string[] = [];
  const draw = (e: Entity): void => {
    switch (e.type) {
      case "group":
        for (const c of e.children) {
          const ce = byId.get(c);
          if (ce) draw(ce);
        }
        return;
      case "polyline":
        if (e.points.length < 2) return;
        parts.push(`<path d="M${e.points.map((p) => `${f(p.x)} ${f(p.y)}`).join(" L")}${e.closed ? " Z" : ""}"/>`);
        return;
      case "circle":
        parts.push(`<circle cx="${f(e.center.x)}" cy="${f(e.center.y)}" r="${f(e.r)}"/>`);
        return;
      case "arc": {
        const a0 = (e.startDeg * Math.PI) / 180;
        const a1 = ((e.startDeg + e.sweepDeg) * Math.PI) / 180;
        const p0 = { x: e.center.x + e.r * Math.cos(a0), y: e.center.y + e.r * Math.sin(a0) };
        const p1 = { x: e.center.x + e.r * Math.cos(a1), y: e.center.y + e.r * Math.sin(a1) };
        const large = Math.abs(e.sweepDeg) > 180 ? 1 : 0;
        const sweep = e.sweepDeg > 0 ? 1 : 0;
        parts.push(`<path d="M${f(p0.x)} ${f(p0.y)} A${f(e.r)} ${f(e.r)} 0 ${large} ${sweep} ${f(p1.x)} ${f(p1.y)}"/>`);
        return;
      }
      default:
        return; // text, labels, dimensions: too small to read in a preview
    }
  };
  for (const e of item.entities) if (!childIds.has(e.id)) draw(e);
  const w = Math.max(item.size.w, 1);
  const h = Math.max(item.size.h, 1);
  const pad = Math.max(w, h) * 0.08;
  const stroke = Math.max(w, h) / 40;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${f(-w / 2 - pad)} ${f(-h / 2 - pad)} ${f(w + 2 * pad)} ${f(h + 2 * pad)}" ` +
    `fill="none" stroke="currentColor" stroke-width="${f(stroke)}" stroke-linejoin="round" stroke-linecap="round">${parts.join("")}</svg>`
  );
}
